<template>
    <div class="vital-signs-report m60-vitals" :class="{ 'localized-layout': !['zh-CN', 'zh-TW', 'zh-HK'].includes($i18n.locale) }" :dir="$i18n.locale === 'ar-AR' ? 'rtl' : 'ltr'">
        <div v-if="loading" class="tab-loading">{{ tr("报告加载中，请稍等") }}</div>
        <div v-else-if="loadError" class="tab-fail">{{ loadError }}</div>
        <template v-else>
            <div class="condition-strip">{{ tr("*为保证测量结果稳定性，需保持面部无遮挡、身体静止！") }}</div>
            <section class="summary-card">
                <div class="score-panel-title"><span>{{ tr("压力指数") }}</span></div>
                <div class="gauge-shell">
                    <div class="gauge-ticks"></div>
                    <svg class="gauge-svg" viewBox="0 0 142 142" aria-hidden="true">
                        <circle
                            class="gauge-track"
                            cx="71"
                            cy="71"
                            :r="gaugeRadius"
                            fill="none"
                            stroke="rgba(102,119,151,0.5)"
                            stroke-width="22"
                        />
                        <circle
                            class="gauge-progress"
                            cx="71"
                            cy="71"
                            :r="gaugeRadius"
                            fill="none"
                            :stroke="stressColor"
                            stroke-width="22"
                            stroke-linecap="butt"
                            :stroke-dasharray="gaugeCircumference"
                            :stroke-dashoffset="gaugeDashOffset"
                            transform="rotate(-90 71 71)"
                        />
                    </svg>
                    <div class="gauge-hole">
                        <div class="gauge-inner" :style="gaugeInnerStyle">
                            <strong>{{ gaugeTitle }}</strong>
                        </div>
                    </div>
                </div>
                <div class="stress-current-state" :style="{ color: stressColor }">
                    {{ stressName }}
                </div>
                <div class="score-levels">
                    <span
                        v-for="item in levels"
                        :key="item.level"
                        class="score-level"
                        :class="{ 'is-active': stressLevel === item.level }"
                        :style="{ color: stressLevel === item.level ? item.color : '' }"
                    >
                        <i :style="{ background: item.color }"></i><span class="score-level-label">{{ item.name }}</span>
                    </span>
                </div>
            </section>

            <!-- 对比栏：对齐红外历史选择（不用 DateSelect，避免历史报告进来算成「无记录」） -->
            <div v-if="!isFirstMeasure" class="compare-bar" @click.stop>
                <span class="compare-time">
                    <button type="button" @click.stop="toggleHistoryPanel">
                        {{ selectedHistory.label || tr('选择对比') }}
                    </button>
                    <div v-show="historyOpen && historyList.length" class="history-panel">
                        <button
                            v-for="item in historyList"
                            :key="item.scanId"
                            class="history-option"
                            type="button"
                            @click="onSelectHistory(item)"
                        >
                            <span class="history-date">{{ item.date }}</span>
                            <span class="history-time">{{ item.time }}</span>
                        </button>
                        <button
                            v-if="historyHasMore"
                            class="history-load-more"
                            type="button"
                            :disabled="historyLoadingMore"
                            @click.stop="loadMoreHistory"
                        >
                            {{ historyLoadingMore ? tr('加载中...') : tr('加载更多') }}
                        </button>
                        <div v-else class="history-no-more">{{ tr("没有更多了") }}</div>
                    </div>
                </span>
                <span class="compare-mid">{{ tr("对比") }}</span>
                <span class="compare-time is-current">{{ currentTimeLabel }} ({{ tr('本次') }})</span>
            </div>

            <section
                v-for="metric in displayMetrics"
                :key="metric.key"
                class="metric-card"
            >
                <div
                    v-if="metric.showDelta && metric.deltaText && metric.deltaText !== '--'"
                    class="metric-delta components-box"
                >
                    <span>{{ tr('对比上次') }}</span>
                    <img v-if="deltaDirection(metric) === 'up'" src="@/assets/images/m60s-domestic/icon1_rise.png" alt />
                    <img v-else-if="deltaDirection(metric) === 'flat'" src="@/assets/images/m60s-domestic/icon1_just.png" alt />
                    <img v-else src="@/assets/images/m60s-domestic/icon1_decline.png" alt />
                    <span class="diff-val">{{ deltaValue(metric) }}</span>
                </div>
                <div v-else class="components-no-box"></div>
                <div class="metric-head">
                    <span class="metric-ico">{{ metricBadge(metric.key) }}</span>
                    <span class="metric-name">
                        {{ metric.name }}
                        <template v-if="metric.unit">
                            <span class="unit">&nbsp;/&nbsp;</span>
                            <span class="unit">{{ metric.unit }}</span>
                        </template>
                        <img
                            class="help-icon"
                            src="@/assets/images/m60s-domestic/report/help.png"
                            alt
                            @click.stop="openDef(metric)"
                        />
                    </span>
                </div>
                <div class="metric-value">
                    <div class="toolip-box" :class="metric.statusClass || ''" :style="valuePillStyle(metric)">
                        <span class="latestData-val">{{ metric.displayValue || '--' }}</span>
                    </div>
                    <!-- HRV 等无评估状态时不展示占位 --，避免数值旁多余灰字 -->
                    <small v-if="metric.statusName && metric.statusName !== '--'">{{ metric.statusName }}</small>
                </div>
                <div v-if="metric.value !== null && metric.ticks && metric.ticks.length" class="range">
                    <div class="range-nums">
                        <span
                            v-for="item in rangeTicks(metric)"
                            :key="`${metric.key}-tick-${item.value}-${item.x}`"
                            :style="{ left: item.x + '%' }"
                        >{{ item.value }}</span>
                    </div>
                    <div class="range-bar">
                        <span
                            v-for="(color, index) in (metric.colors || [])"
                            :key="`${metric.key}-seg-${index}`"
                            class="seg"
                            :class="colorClass(color)"
                            :style="segFlexStyle(metric, index)"
                        ></span>
                        <i
                            class="marker"
                            aria-hidden="true"
                            :style="{ left: markerLeft(metric) }"
                        ></i>
                    </div>
                    <div class="range-labels">
                        <span
                            v-for="(label, index) in rangeLabels(metric)"
                            :key="`${metric.key}-label-${index}`"
                            :style="segFlexStyle(metric, index)"
                        >{{ label }}</span>
                    </div>
                </div>
            </section>

            <p class="disclaimer">{{ report.disclaimer || overall.disclaimer || defaultDisclaimer }}</p>
        </template>

        <!-- 定义说明：复用体成分 body-composition-popup 样式 -->
        <van-popup v-model="defVisible" class="body-composition-popup" popup-transition="popup-fade">
            <div class="popup-container" @touchmove.prevent>
                <div class="content pop center">
                    <h4>{{ defTitle }}</h4>
                </div>
                <div class="first-intro content center">
                    <p>{{ defText }}</p>
                </div>
                <div class="pop-btn-action" @click="defVisible = false">{{ tr('知道了') }}</div>
            </div>
        </van-popup>
    </div>
</template>

<script>
import { domesticText, vitalView, historyTime } from '@/assets/js/m60sDomesticReport'

const LEVELS = [
    { level: 0, name: '放松', color: '#7C8CFF' },
    { level: 1, name: '低压力', color: '#59AAF8' },
    { level: 2, name: '正常压力', color: '#43C98B' },
    { level: 3, name: '压力偏高', color: '#E5B62B' },
    { level: 4, name: '高压力', color: '#F97316' },
    { level: 5, name: '极高压力', color: '#A61E1E' },
]

const BADGES = {
    hr: 'H',
    spo2: 'O',
    si: 'S',
    hrv: 'V',
    rr: 'R',
}

/** 与 WellnessHub 一致：静态心率 → HRV → 压力指数 → 呼吸频率 → 血氧 */
const METRIC_ORDER = ['hr', 'hrv', 'rr', 'spo2']

const COLOR_CLASS = {
    '#7c8cff': 'purple',
    '#59aaf8': 'blue',
    '#43c98b': 'green',
    '#e5b62b': 'yellow',
    '#f97316': 'orange',
    '#cf2b2b': 'red',
    '#a61e1e': 'deepred',
}

/** demo 标准范围色条不等分宽度（与刻度边界对齐） */
const RANGE_COLS = {
    hr: '1fr 2.5fr 1fr',
    rr: '1fr 2.5fr 1fr',
    spo2: '1fr 1.2fr 2.7fr',
    si: '.7fr .5fr 1.2fr 1.7fr 1.3fr 1fr',
}

/** demo 下方文案（比接口原文更短，避免挤爆） */
const RANGE_LABELS = {
    hr: ['心率偏低', '正常', '心率偏高'],
    spo2: ['明显偏低', '偏低', '正常'],
    si: ['放松', '低', '正常', '偏高', '高', '极高'],
    rr: ['呼吸偏低', '正常', '呼吸偏高'],
}

const DEFAULT_DISCLAIMER =
    '声明：生命体征测量结果仅用于健康参考和趋势观察，不作为医学诊断依据。'

function parseFrWeights(cols) {
    return String(cols || '')
        .trim()
        .split(/\s+/)
        .map((part) => parseFloat(part))
        .filter((n) => Number.isFinite(n) && n > 0)
}

/** 按 fr 权重求各段右边界百分比，供刻度数字落点（与 demo 一致） */
function boundaryPercents(cols) {
    const weights = parseFrWeights(cols)
    const total = weights.reduce((sum, n) => sum + n, 0)
    if (!total) return []
    const result = []
    let acc = 0
    for (let i = 0; i < weights.length - 1; i += 1) {
        acc += weights[i]
        result.push((acc / total) * 100)
    }
    return result
}

/** 解析刻度成可比较数字（血压取收缩压） */
function parseTickNumber(tick) {
    const raw = String(tick || '')
    const matched = raw.match(/-?\d+(\.\d+)?/)
    return matched ? Number(matched[0]) : NaN
}

/**
 * 按「不等分色段」重算指针位置。
 * 接口 position 按等分色段计算，和 demo 的 fr 视觉宽度对不上，必须前端重算。
 * 校验：HR 66 → ~30.56%；SpO2 94 → 40%；SI 128 → ~33.38%；BP 117/89 → ~53%
 */
function visualMarkerPercent(metric, cols) {
    const weights = parseFrWeights(cols)
    const total = weights.reduce((sum, n) => sum + n, 0)
    if (!total || !weights.length) {
        const fallback = Number(metric.position)
        return Number.isFinite(fallback) ? fallback : 50
    }

    const ticks = metric.ticks || []
    const tickNums = ticks.map(parseTickNumber).filter((n) => Number.isFinite(n))
    // 兼容 value 缺失或字符串（微信端偶发只带 displayValue）
    let value = Number(metric.value)
    if (!Number.isFinite(value)) {
        value = parseTickNumber(metric.displayValue)
    }
    if (!Number.isFinite(value) || !tickNums.length) {
        const fallback = Number(metric.position)
        return Number.isFinite(fallback) ? fallback : 50
    }
    return markerPercentByValue(value, tickNums, weights, total, metric.key)
}

/** 将数值映射到不等分色段上的百分比位置 */
function markerPercentByValue(value, tickNums, weights, total, metricKey) {
    const segCount = weights.length
    let segIndex = 0
    let ratio = 0.5
    const last = tickNums[tickNums.length - 1]
    // 与 evaluate* 对齐（默认左闭右开 [lo, hi)）：
    // - 首刻度：value < t0 → 第一段（SpO2 90→偏低，SI 30→低压力）
    // - 中间刻度：落在刻度上时进下一段（SI 50→正常，而非低压力末尾）
    // - 末刻度：hr/rr 为正常上界（含）；spo2 等为下一段起点（含）
    // - SI 特判：500 为「偏高」上界（含），与 evaluateSi value<=500 一致
    const lastInclusive = metricKey === 'hr' || metricKey === 'rr'
    const pastLast = lastInclusive ? value > last : value >= last

    if (value < tickNums[0]) {
        segIndex = 0
        const span = Math.max(tickNums[0], 1)
        ratio = Math.max(0.08, Math.min(0.92, value / span))
    } else if (pastLast) {
        segIndex = segCount - 1
        // 恰等于末刻度：落在最后一段起点（SpO2 95→正常左缘，勿用 0.2 起步甩到段中/段尾）
        if (value <= last) {
            ratio = 0.04
        } else if (metricKey === 'spo2') {
            // 血氧常见展示区间约 95–100
            ratio = Math.max(0.04, Math.min(0.96, (value - last) / 5))
        } else {
            const over = (value - last) / Math.max(last, 1)
            ratio = Math.max(0.08, Math.min(0.92, 0.2 + over))
        }
    } else {
        for (let i = 0; i < tickNums.length - 1; i += 1) {
            const lo = tickNums[i]
            const hi = tickNums[i + 1]
            const upperInclusive =
                (lastInclusive && i === tickNums.length - 2) ||
                (metricKey === 'si' && hi === 500)
            const inRange = value >= lo && (upperInclusive ? value <= hi : value < hi)
            if (inRange) {
                segIndex = i + 1
                ratio = hi === lo ? 0.5 : (value - lo) / (hi - lo)
                break
            }
        }
    }

    segIndex = Math.max(0, Math.min(segCount - 1, segIndex))
    ratio = Math.max(0.04, Math.min(0.96, ratio))

    let start = 0
    for (let i = 0; i < segIndex; i += 1) {
        start += (weights[i] / total) * 100
    }
    const width = (weights[segIndex] / total) * 100
    return Number((start + ratio * width).toFixed(2))
}

export default {
    name: 'VitalSignsItem',
    props: {
        reportData: { type: Object, default: () => ({}) },
        massInfo: {
            type: Object,
            default() {
                return {}
            },
        },
        isCreateTime: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: true,
            loadError: '',
            tcScanId: '',
            reportLoadToken: 0,
            historyList: [],
            historyHasMore: false,
            historyLoadingMore: false,
            historyPageSize: 10,
            /** 已从接口拉取的条数（不含 syncCompare 兜底插入），用于 skip */
            historyFetchedCount: 0,
            /** 历史列表时间锚点：固定为「本次」测量时间，避免切对比后被 compare 时间带偏 */
            historyAnchorTime: 0,
            /** 历史列表请求序号，丢弃过期响应，避免切对比时旧请求覆盖列表 */
            historyLoadToken: 0,
            selectedHistory: { label: '', scanId: '', date: '', time: '', createTime: 0 },
            historyOpen: false,
            defaultDisclaimer: DEFAULT_DISCLAIMER,
            report: {
                overall: { name: '--', color: '#43C98B', level: 0, ringPercent: 22, conclusion: '', highlight: '', disclaimer: '' },
                metrics: [],
                disclaimer: '',
                hasHistory: false,
                isFirstMeasure: true,
            },

            defVisible: false,
            defTitle: '',
            defText: '',
        }
    },
    computed: {
        levels() { return LEVELS.map(item => ({ ...item, name: this.tr(item.name) })) },
        overall() {
            return this.report.overall || {}
        },
        stressMetric() {
            return (this.report.metrics || []).find((item) => item.key === 'si') || null
        },
        hasStressValue() {
            const value = this.stressMetric && this.stressMetric.value
            return value !== null && typeof value !== 'undefined' && value !== '' && Number.isFinite(Number(value))
        },
        stressLevel() {
            if (!this.hasStressValue) return -1
            const value = Number(this.stressMetric.value)
            return value < 30 ? 0 : value < 50 ? 1 : value < 150 ? 2 : value <= 500 ? 3 : value < 900 ? 4 : 5
        },
        stressColor() {
            return this.levels[this.stressLevel] ? this.levels[this.stressLevel].color : '#9AA7B8'
        },
        stressName() {
            return this.levels[this.stressLevel] ? this.levels[this.stressLevel].name : '--'
        },
        stressValue() {
            return this.hasStressValue ? String(this.stressMetric.value) : '--'
        },
        scorePercent() {
            if (!this.hasStressValue || Number(this.stressMetric.value) <= 0) return 0
            const metric = { ...this.stressMetric, ticks: [30, 50, 150, 500, 900] }
            return Math.max(0, Math.min(100, visualMarkerPercent(metric, RANGE_COLS.si)))
        },
        isFirstMeasure() {
            if (!this.report) return true
            if (typeof this.report.isFirstMeasure === 'boolean') return this.report.isFirstMeasure
            return !this.report.hasHistory
        },
        currentTimeLabel() {
            const t = this.report.measureTime || this.massInfo.createTime
            if (!t) return this.isCreateTime || '--'
            return historyTime(t).label
        },
        displayMetrics() {
            const list = this.report.metrics || []
            const map = {}
            list.forEach((item) => {
                if (item && item.key) map[item.key] = item
            })
            const ordered = METRIC_ORDER.map((key) => map[key]).filter(Boolean)
            if (ordered.length) return ordered.filter((item) => item.key !== 'bp' && item.key !== 'si')
            return list.filter((item) => item.key !== 'bp' && item.key !== 'si')
        },
        /** SVG 圆环半径（描边中心），避开微信 conic-gradient 方向错乱 */
        gaugeRadius() {
            return 60
        },
        gaugeCircumference() {
            return 2 * Math.PI * this.gaugeRadius
        },
        gaugeDashOffset() {
            return this.gaugeCircumference * (1 - this.scorePercent / 100)
        },
        gaugeInnerStyle() {
            return { color: this.stressColor }
        },
        gaugeTitle() {
            return this.stressValue
        },
    },
    watch: {
        reportData: { immediate: true, handler() { this.tcScanId = ''; this.historyFetchedCount = 10; this.syncReport() } },
        '$i18n.locale'() { this.syncReport() },
    },
    mounted() {
        document.addEventListener('click', this.closeHistoryPanel)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeHistoryPanel)
    },
    methods: {
        tr(text) { return domesticText(this.$i18n.locale, text) },
        syncReport() {
            const source = this.reportData;
            const history = Array.isArray(source.compareRecords) ? source.compareRecords : [];
            const selected = history.find(item => item.scanId === this.tcScanId) || history[0];
            this.tcScanId = selected ? selected.scanId : '';
            this.report = vitalView(source, selected, this.$i18n.locale);
            this.historyList = history.slice(0, this.historyFetchedCount || 10).map(item => ({ ...historyTime(item.scanTime), scanId: item.scanId }));
            this.historyHasMore = history.length > this.historyList.length;
            this.selectedHistory = selected ? { ...historyTime(selected.scanTime), scanId: selected.scanId } : { label: '', scanId: '' };
            this.loading = false;
            this.loadError = '';
        },
        metricBadge(key) {
            return BADGES[key] || ''
        },
        colorClass(color) {
            return COLOR_CLASS[String(color || '').toLowerCase()] || ''
        },
        resolveCols(metric) {
            const count = Math.max((metric.colors && metric.colors.length) || 1, 1)
            return RANGE_COLS[metric.key] || Array.from({ length: count }, () => '1fr').join(' ')
        },
        /** flex 权重，避免微信里 grid + fr 失效导致等宽错位 */
        segFlexStyle(metric, index) {
            const weights = parseFrWeights(this.resolveCols(metric))
            const flex = weights[index] || 1
            return { flex: `${flex} 1 0`, WebkitBoxFlex: flex }
        },
        markerLeft(metric) {
            const cols = this.resolveCols(metric)
            return `${visualMarkerPercent(metric, cols)}%`
        },
        /** 刻度数字落在色段边界上，而不是均分 */
        rangeTicks(metric) {
            const ticks = metric.ticks || []
            if (!ticks.length) return []
            const cols = this.resolveCols(metric)
            const bounds = boundaryPercents(cols)
            if (bounds.length === ticks.length) {
                return ticks.map((value, index) => ({
                    value,
                    x: Number(bounds[index].toFixed(2)),
                }))
            }
            // 兜底：均分（仅当色段配置与 ticks 数量不匹配时）
            const segmentCount = ticks.length + 1
            return ticks.map((value, index) => ({
                value,
                x: Number((((index + 1) * 100) / segmentCount).toFixed(2)),
            }))
        },
        rangeLabels(metric) {
            if (RANGE_LABELS[metric.key]) return RANGE_LABELS[metric.key].map(label => {
                const text = this.tr(label)
                // Break the Finnish compound at its word boundary on narrow range segments.
                return this.$i18n.locale === 'fi-FI' && metric.key === 'rr'
                    ? text.replace('hengitystiheys', 'hengitys\u00adtiheys')
                    : text
            })
            return metric.labels || []
        },
        valuePillStyle(metric) {
            // 有评估状态时优先用接口 statusColor（放松 #7C8CFF），避免 purple 被映射成 .low 蓝
            if (!metric.statusName || metric.statusName === '--' || !metric.statusColor) return {}
            return { background: metric.statusColor }
        },
        parseDelta(text) {
            const raw = String(text || '--').trim()
            if (!raw || raw === '--') return { direction: 'flat', value: '--' }
            if (raw.includes('↑')) {
                return { direction: 'up', value: raw.replace(/↑\s*/g, '').trim() || '--' }
            }
            if (raw.includes('↓')) {
                return { direction: 'down', value: raw.replace(/↓\s*/g, '').trim() || '--' }
            }
            return { direction: 'flat', value: raw.replace(/→\s*/g, '').trim() || '--' }
        },
        deltaDirection(metric) {
            return this.parseDelta(metric.deltaText).direction
        },
        deltaValue(metric) {
            return this.parseDelta(metric.deltaText).value
        },
        openDef(metric) {
            this.defTitle = metric.name
            this.defText = metric.definition || this.tr('暂无说明')
            this.defVisible = true
        },
        closeHistoryPanel() {
            this.historyOpen = false
        },
        toggleHistoryPanel() {
            if (!this.historyList.length) return
            this.historyOpen = !this.historyOpen
        },
        onSelectHistory(item) {
            if (!item || !item.scanId) return;
            this.tcScanId = item.scanId;
            this.historyOpen = false;
            this.syncReport();
        },
        loadMoreHistory() { this.historyFetchedCount += 10; this.syncReport() },
    },
}
</script>

<style lang="less" scoped>
.vital-signs-report {
    --bg: #10142b;
    --panel: #14284f;
    --line: #3b7bad;
    --cyan: #02d7e4;
    --muted: #8fa2c0;
    --purple: #7c8cff;
    --blue-low: #59aaf8;
    --green: #43c98b;
    --yellow: #e5b62b;
    --orange: #f97316;
    --red: #cf2b2b;
    --deepred: #a61e1e;
    /* 对齐体成分 BodyData：左右 13px，避免指标卡右侧多出一截缝隙 */
    padding: 0 13px 30px;
    color: #f0f7ff;
    overflow: visible;
}

.condition-strip {
    margin: 7px 0 7px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #c7d3e9;
    font-size: 13px;
    line-height: 1.5;
}

.summary-card {
    position: relative;
    margin-bottom: 18px;
    padding: 76px 14px 20px;
    overflow: hidden;
    border: 1px solid rgba(71, 166, 224, 0.86);
    border-radius: 8px;
    background:
        radial-gradient(circle at 12% 16%, rgba(158, 70, 148, 0.38), transparent 36%),
        radial-gradient(circle at 88% 54%, rgba(30, 111, 204, 0.32), transparent 34%),
        linear-gradient(145deg, rgba(31, 28, 64, 0.95), rgba(13, 25, 59, 0.96));
    box-shadow:
        inset 0 0 34px rgba(44, 133, 211, 0.16),
        0 0 0 1px rgba(2, 215, 228, 0.08);
}

/* 对齐红外历史对比条 */
.compare-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 0 0 14px;
    padding: 10px 12px;
    border: 1px solid rgba(71, 166, 224, 0.45);
    border-radius: 8px;
    background: rgba(16, 42, 81, 0.66);
    color: var(--muted);
    font-size: 12px;
    overflow: visible;
}

.compare-mid {
    flex: 0 0 auto;
    color: #8aa3be;
}

.compare-time {
    position: relative;
    min-width: 0;
    white-space: nowrap;
    font-size: 11px;
    letter-spacing: -0.02em;

    &.is-current {
        text-align: right;
        color: #c7d3e9;
    }

    > button {
        border: 0;
        padding: 0;
        color: var(--cyan);
        background: transparent;
        font: inherit;
        font-weight: 700;
        white-space: nowrap;

        &::after {
            content: '⌄';
            display: inline-block;
            position: relative;
            top: -6px;
            margin-left: 4px;
            font-size: 14px;
            line-height: 1;
            vertical-align: middle;
        }
    }
}

.history-panel {
    position: absolute;
    left: 0;
    top: calc(100% + 8px);
    z-index: 35;
    display: grid;
    gap: 8px;
    min-width: 168px;
    width: max-content;
    max-width: min(220px, calc(100vw - 48px));
    max-height: 220px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 8px;
    border: 1px solid rgba(86, 151, 210, 0.32);
    border-radius: 12px;
    background: rgba(8, 25, 54, 0.98);
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.34);
}

.history-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    border: 1px solid rgba(2, 215, 228, 0.26);
    border-radius: 8px;
    background: rgba(2, 215, 228, 0.08);
    color: #c9f7ff;
    padding: 8px;
    font-size: 11px;
    text-align: left;
    white-space: nowrap;
}

.history-date,
.history-time {
    display: inline-block;
}

.history-load-more {
    display: block;
    width: 100%;
    border: 1px dashed rgba(86, 151, 210, 0.45);
    border-radius: 8px;
    background: rgba(20, 40, 79, 0.9);
    color: #8fa2c0;
    padding: 8px;
    font-size: 11px;
    text-align: center;
    cursor: pointer;

    &:disabled {
        opacity: 0.65;
        cursor: wait;
    }
}

.history-no-more {
    padding: 6px 4px 2px;
    color: #6f829d;
    font-size: 10px;
    text-align: center;
}

.score-panel-title {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 58px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #f8fbff;
    font-size: 20px;
    font-weight: 900;
}

.score-panel-title::before {
    content: '';
    position: absolute;
    inset: 0 10px 0;
    background: linear-gradient(90deg, rgba(115, 58, 126, 0.62), rgba(34, 39, 91, 0.76), rgba(21, 40, 91, 0.62));
    clip-path: polygon(0 0, 22% 0, 33% 100%, 67% 100%, 78% 0, 100% 0, 100% 50%, 88% 50%, 78% 100%, 22% 100%, 12% 50%, 0 50%);
}

.score-panel-title::after {
    content: '';
    position: absolute;
    left: 29%;
    right: 29%;
    bottom: 2px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(2, 215, 228, 0.95), transparent);
    box-shadow: 0 0 12px rgba(2, 215, 228, 0.78);
}

.score-panel-title span {
    position: relative;
    z-index: 1;
}

.gauge-shell {
    position: relative;
    width: 176px;
    height: 176px;
    margin: 16px auto 12px;
}

.gauge-ticks {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 50%;
    background: repeating-conic-gradient(from -90deg, rgba(170, 195, 232, 0.58) 0 2deg, transparent 2deg 10deg);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 13px), #000 calc(100% - 12px));
    mask: radial-gradient(farthest-side, transparent calc(100% - 13px), #000 calc(100% - 12px));
    opacity: 0.78;
    pointer-events: none;
}

.gauge-svg {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 142px;
    height: 142px;
    margin: -71px 0 0 -71px;
    z-index: 1;
    overflow: visible;
}

.gauge-track,
.gauge-progress {
    fill: none;
}

.gauge-hole {
    position: absolute;
    left: 50%;
    top: 50%;
    z-index: 2;
    width: 98px;
    height: 98px;
    margin: -49px 0 0 -49px;
    border-radius: 50%;
    background: #171b39;
    box-shadow: inset 0 0 22px rgba(0, 0, 0, 0.2);
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    -webkit-box-align: center;
    -webkit-align-items: center;
    align-items: center;
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    justify-content: center;
}

.gauge-inner {
    position: relative;
    z-index: 1;
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -webkit-flex-direction: column;
    flex-direction: column;
    -webkit-box-align: center;
    -webkit-align-items: center;
    align-items: center;
    -webkit-box-pack: center;
    -webkit-justify-content: center;
    justify-content: center;
}

.gauge-inner strong {
    font-size: 26px;
    line-height: 1;
}

.gauge-inner span {
    color: #c7d3e9;
    font-size: 17px;
    font-weight: 800;
    margin-top: 2px;
}

.stress-current-state {
    margin: 8px auto 12px;
    text-align: center;
    font-size: 22px;
    font-weight: 700;
}

.score-levels {
    max-width: 320px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    justify-content: center;
    gap: 12px;
    margin: 8px auto 18px;
    color: #c7d3e9;
    font-size: 14px;
    font-weight: 900;
}

.score-level {
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    -webkit-box-align: center;
    -webkit-align-items: center;
    align-items: center;
    margin: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    min-width: 0;
    box-sizing: border-box;
}

.score-level i {
    width: 8px;
    height: 8px;
    margin-right: 7px;
    -webkit-flex: 0 0 auto;
    flex: 0 0 auto;
    border-radius: 1px;
    background: #43c98b;
}

.score-level.is-active {
    font-weight: 900;
}

.score-alert {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    color: #e7efff;
    background: rgba(87, 123, 169, 0.54);
    font-size: 12px;
    line-height: 1.5;
    text-align: left;
}

.score-alert span {
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
    border: 2px solid rgba(214, 226, 245, 0.65);
    border-radius: 50%;
    color: rgba(229, 239, 255, 0.9);
    font-size: 12px;
    font-weight: 900;
}

.score-alert p {
    margin: 0;
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
}

.score-divider {
    position: relative;
    margin-top: 18px;
    border-top: 1px dashed rgba(176, 199, 231, 0.32);
}

.score-divider span {
    position: absolute;
    left: 50%;
    top: -10px;
    width: 22px;
    height: 22px;
    transform: translateX(-50%);
    border-radius: 50%;
    background: conic-gradient(from 45deg, #1677ff, #7d4dff, #ff58c7, #1677ff);
    box-shadow: 0 0 12px rgba(82, 127, 255, 0.4);
}

/* 指标卡：对齐体成分 BodyDataItem（bj31 外框本身不加左右内边距） */
.metric-card {
    position: relative;
    width: 353px;
    max-width: 100%;
    box-sizing: border-box;
    margin: 0 auto 10px;
    padding: 4px 0 14px 0px;
    background: url(../../assets/images/m60s-domestic/report/bj31.png) center no-repeat;
    background-size: 100% 100%;
    border: 0;
    box-shadow: none;
}

.metric-delta.components-box {
    color: #bcd0e5;
    background-image: url(../../assets/images/m60s-domestic/report/contrast_last.png);
    background-repeat: no-repeat;
    background-position: right;
    background-size: 110px 21px;
    position: relative;
    top: -3px;
    text-align: right;
    margin-right: 1px;
    min-height: 21px;

    img {
        width: 14px;
        height: 14px;
        margin-left: 2px;
        position: relative;
        top: 2px;
        vertical-align: middle;
    }

    span {
        font-size: 11px;
        margin-right: 2px;
    }

    .diff-val {
        font-size: 11px;
        color: #bcd0e5;
    }
}

.components-no-box {
    height: 14px;
    width: 110px;
}

.metric-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 10px 10px;
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.2;
}

.metric-name {
    display: block;
    overflow-wrap: anywhere;
    min-width: 0;
    font-size: 14px;
    font-weight: 400;

    .unit {
        color: #8aa3be;
        word-break: keep-all;
    }
}

.metric-ico {
    width: 16px;
    height: 16px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    margin: 0 2px 0 0;
    border: 1px solid var(--cyan);
    border-radius: 50%;
    color: var(--cyan);
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.help-icon {
    width: 10px;
    height: 10px;
    margin-left: 4px;
    vertical-align: middle;
    cursor: pointer;
}

/* 胶囊固定在中列居中，状态文字放右列，避免整体居中导致胶囊偏左 */
.metric-value {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    column-gap: 8px;
    margin: 8px 0 14px;
}

.metric-value .toolip-box {
    grid-column: 2;
    display: inline-block;
    min-width: 34px;
    width: 90px;
    height: 22px;
    line-height: 22px;
    padding: 0 3px;
    border-radius: 10px;
    background: #33c070;
    color: #fff;
    text-align: center;
    font-size: 11px;

    .latestData-val {
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
    }

    &.low {
        background: var(--blue-low);
    }

    &.watch {
        background: var(--yellow);
        color: #111827;
    }

    &.warn {
        background: var(--orange);
    }

    &.risk {
        background: var(--deepred);
    }

    &.ok {
        background: var(--green);
    }
}

.metric-value small {
    grid-column: 3;
    justify-self: start;
    min-width: 0;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
    color: rgba(255, 255, 255, 0.4);
    font-size: 12px;
}

.range {
    direction: ltr;
    position: relative;
}

.range-nums {
    position: relative;
    height: 16px;
    margin: 0 14px 16px;
    color: rgba(255, 255, 255, 0.4);
    font-size: 12px;
    line-height: 1.2;
}

.range-nums span {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    white-space: nowrap;
}

.range-bar {
    position: relative;
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    gap: 3px;
    height: 7px;
    margin: 6px 14px 8px;
}

.seg {
    display: block;
    min-width: 0;
    height: 100%;
    border-radius: 99px;
    background: #7aa5dc;
}

.seg.purple { background: #7c8cff; }
.seg.blue { background: #59aaf8; }
.seg.green { background: #43c98b; }
.seg.yellow { background: #e5b62b; }
.seg.orange { background: #f97316; }
.seg.red { background: #cf2b2b; }
.seg.deepred { background: #a61e1e; }

.marker {
    position: absolute;
    left: 50%;
    top: -12px;
    width: 0;
    height: 0;
    margin: 0;
    padding: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 10px solid #02d7e4;
    background: transparent;
    -webkit-transform: translateX(-50%);
    transform: translateX(-50%);
    filter: drop-shadow(0 0 8px rgba(2, 215, 228, 0.55));
    font-style: normal;
    pointer-events: none;
}

.range-labels {
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
    gap: 3px;
    padding: 0 14px;
    color: rgba(255, 255, 255, 0.4);
    font-size: 12px;
    line-height: 1.25;
    text-align: center;
}

.range-labels span {
    min-width: 0;
    overflow-wrap: anywhere;
    white-space: normal;
}

.disclaimer {
    margin: 8px 0 0;
    color: var(--muted);
    font-size: 12px;
    line-height: 1.7;
    text-align: left;
}

.tab-loading,
.tab-fail {
    padding: 24px 0;
    text-align: center;
    color: var(--muted);
}

/* 与体成分 BodyDataItem 弹层标题一致 */
.popup-container {
    font-family: PingFangSC-Regular, PingFang SC;
    font-weight: 400;
    color: #000000;

    .content {
        h4 {
            color: #000000;
            font-size: 17px;
            font-weight: 540;
            margin-top: 10px;
            font-family: PingFangSC-Regular, PingFang SC;
        }
    }

    .center {
        text-align: center !important;
    }
}

/* Long translations need explicit text alignment and a badge that fits its content. */
.localized-layout .score-levels {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.localized-layout .score-level {
    align-items: flex-start;
    text-align: start;
    line-height: 1.4;
}
.localized-layout .score-level i {
    margin-top: calc(0.7em - 4px);
}
.localized-layout .score-level-label {
    flex: 1 1 0;
    min-width: 0;
}
.localized-layout .metric-delta.components-box {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: fit-content;
    min-width: 110px;
    max-width: calc(100% - 2px);
    box-sizing: border-box;
    margin-left: auto;
    padding: 2px 3px 2px 20px;
    background-size: 100% 100%;
    text-align: end;
    line-height: 17px;
}
.localized-layout .metric-delta > span:first-child { min-width: 0; overflow-wrap: anywhere; }
.localized-layout .metric-delta img { flex-shrink: 0; top: 0; }
.localized-layout .metric-delta .diff-val { flex-shrink: 0; white-space: nowrap; }

/* RTL spacing belongs between the swatch and label, not outside the swatch. */
[dir="rtl"] .score-level i { margin-right: 0; margin-left: 7px; }
[dir="rtl"].localized-layout .metric-delta.components-box {
    margin-left: 1px;
    margin-right: auto;
}
[dir="rtl"] .compare-time { text-align: start; }
[dir="rtl"] .range-labels span { direction: rtl; }
[dir="rtl"] .history-panel { left: auto; right: 0; }
.history-option { direction: ltr; }
.compare-time > button, .compare-time.is-current { unicode-bidi: plaintext; }
@media (max-width: 360px) {
    .compare-time { white-space: normal; overflow-wrap: anywhere; }
    .compare-top { grid-template-columns: minmax(0, 1fr) 88px minmax(0, 1fr); }
}
</style>
